import type { BillingPlan } from "@pex/billing";
import { getSemanticPlanKey } from "@pex/billing";

// ─── Marketing copy (hardcoded by design) ─────────────────────────────────────
// Feature lists and tier names are marketing copy, keyed by the semantic plan
// key shared with Bridge (getSemanticPlanKey). Prices and checkout codes come
// from the DB; the price strings below are fallbacks so the pricing page still
// renders fully if the plan fetch is unavailable (falls back to the legacy CTA
// too). Shared by the pricing cards and the /checkout order summary.

export interface PlanCardCopy {
  fallbackAnnualBilled: string;
  fallbackAnnualMonthly: string;
  fallbackMonthly: string;
  features: readonly string[];
  key: string;
  name: string;
}

export const COMMUNITY_PLANS: readonly PlanCardCopy[] = [
  {
    name: "Community Starter",
    key: "community-starter",
    fallbackMonthly: "R1,000",
    fallbackAnnualMonthly: "R800",
    fallbackAnnualBilled: "R9,600",
    features: [
      "1 x recovery group",
      "2 x lead facilitators",
      "ACADEMY access",
      "BRIDGE access",
      "Recovery group resources",
    ],
  },
  {
    name: "Community Growth",
    key: "community-growth",
    fallbackMonthly: "R2,000",
    fallbackAnnualMonthly: "R1,600",
    fallbackAnnualBilled: "R19,200",
    features: [
      "2 x recovery groups",
      "1 x specialised programme*",
      "4 x lead facilitators",
      "ACADEMY access",
      "BRIDGE access",
      "Recovery group resources",
      "Specialised programme resources",
    ],
  },
  {
    name: "Community Established",
    key: "community-established",
    fallbackMonthly: "R3,500",
    fallbackAnnualMonthly: "R2,800",
    fallbackAnnualBilled: "R33,600",
    features: [
      "3 x recovery groups",
      "2 x specialised programmes*",
      "6 x lead facilitators",
      "ACADEMY access",
      "BRIDGE access",
      "Recovery group resources",
      "Specialised programme resources",
    ],
  },
];

export const PROGRAMME_PLANS: readonly PlanCardCopy[] = [
  {
    name: "Programme Starter",
    key: "programme-starter",
    fallbackMonthly: "R1,000",
    fallbackAnnualMonthly: "R800",
    fallbackAnnualBilled: "R9,600",
    features: [
      "1 x specialised programme*",
      "2 x lead facilitators",
      "ACADEMY access",
      "BRIDGE access",
      "Specialised programme resources",
    ],
  },
  {
    name: "Programme Growth",
    key: "programme-growth",
    fallbackMonthly: "R2,000",
    fallbackAnnualMonthly: "R1,600",
    fallbackAnnualBilled: "R19,200",
    features: [
      "2 x specialised programmes*",
      "4 x lead facilitators",
      "ACADEMY access",
      "BRIDGE access",
      "Specialised programme resources",
    ],
  },
  {
    name: "Programme Established",
    key: "programme-established",
    fallbackMonthly: "R3,000",
    fallbackAnnualMonthly: "R2,400",
    fallbackAnnualBilled: "R28,800",
    features: [
      "3 x specialised programmes*",
      "6 x lead facilitators",
      "ACADEMY access",
      "BRIDGE access",
      "Specialised programme resources",
    ],
  },
];

export const ALL_PLAN_COPY: readonly PlanCardCopy[] = [
  ...COMMUNITY_PLANS,
  ...PROGRAMME_PLANS,
];

export type BillingPeriod = "monthly" | "annual";

// ─── DB plan lookup ───────────────────────────────────────────────────────────

export function formatRand(amount: number): string {
  return `R${amount.toLocaleString("en-US")}`;
}

/** Finds the DB plan for a marketing card at the given billing period. */
export function findDbPlan(
  plans: BillingPlan[],
  cardKey: string,
  billing: BillingPeriod
): BillingPlan | null {
  const interval = billing === "monthly" ? "monthly" : "annually";
  return (
    plans.find(
      (p) => p.interval === interval && getSemanticPlanKey(p) === cardKey
    ) ?? null
  );
}

export interface CardPricing {
  annualBilled: string;
  annualMonthly: string;
  monthly: string;
}

/** DB prices when available, marketing fallbacks otherwise. */
export function resolvePricing(
  plans: BillingPlan[],
  card: PlanCardCopy
): CardPricing {
  const monthlyPlan = findDbPlan(plans, card.key, "monthly");
  const annualPlan = findDbPlan(plans, card.key, "annual");
  return {
    monthly: monthlyPlan
      ? formatRand(monthlyPlan.amount)
      : card.fallbackMonthly,
    annualMonthly: annualPlan
      ? formatRand(Math.round(annualPlan.amount / 12))
      : card.fallbackAnnualMonthly,
    annualBilled: annualPlan
      ? formatRand(annualPlan.amount)
      : card.fallbackAnnualBilled,
  };
}

/** Marketing copy for a DB plan (name + feature list), or null if unmapped. */
export function findPlanCopy(plan: BillingPlan): PlanCardCopy | null {
  const key = getSemanticPlanKey(plan);
  return ALL_PLAN_COPY.find((c) => c.key === key) ?? null;
}
